import type {
  Answer,
  ComputableContractsQuestions,
} from 'ts/types/coverage-questions.types';
import type {
  GetCoveredServicesByCriteria,
  RemoveExtraDataFromAnswers,
  RemoveLastAnswerReturn,
} from './types';
import getHours from 'date-fns/getHours';
import getYear from 'date-fns/getYear';
import isWeekend from 'date-fns/isWeekend';
import type { Entries, PassengersData, VehicleDetailsData } from 'ts/types';
import type { DecisionTreeDataResultModel } from 'redux/actions/caseCreationActions.types';

export const answersNotAllowedToRemove = [
  'BABY',
  'DISABILITY',
  'DISTANCE_FROM_HOME_ROAD',
  'DISTANCE_TO_DESTINATION_ROAD',
  'HOUR',
  'INCIDENTS',
  'PET',
  'PREGNANT',
  'WEEKEND',
  'NUMBER_OF_PASSENGERS',
  'IMMOBILIZED_VEHICLE',
  'CASE_SOURCE',
  'COUNTRIES',
  'VEHICLE_TYPE',
  'VEHICLE_WEIGHT',
  'HAS_TRAILER',
  'VHR',
  'FRANCHISE',
  'VEHICLE_AGE',
  'HIGHWAY_INCIDENT',
  'SECOND_TOWING_NEEDED',
] as const;

export const getCoveredServicesByCriteria: GetCoveredServicesByCriteria = (
  coverageResponses,
  criteria,
  supportedServices,
) => {
  let data = [...coverageResponses];

  if (supportedServices && supportedServices.length > 0) {
    data = data.filter(val => supportedServices.includes(val.service));
  }

  data = data.filter(({ result }) => criteria.includes(result));

  return data;
};

export const removeExtraDataFromAnswers: RemoveExtraDataFromAnswers = answers =>
  answers.length > 0
    ? answers.map(answer => ({
        id: answer.id,
        value: answer.value,
      }))
    : undefined;

export const isAnswerRemovable = (answers: Answer[]) => {
  const lastAnswer = answers[answers.length - 1];
  if (!lastAnswer) {
    return false;
  }
  return !answersNotAllowedToRemove.includes(lastAnswer.id as any);
};

export const removeLastAnswerFromCoverage = (
  answers: Answer[] = [],
): RemoveLastAnswerReturn => {
  if (!isAnswerRemovable(answers)) {
    return {
      answers,
      wasAnswerRemoved: false,
    };
  }
  const filteredAnswers = answers.slice(0, answers.length - 1);
  return {
    answers: filteredAnswers,
    wasAnswerRemoved: true,
  };
};

const ccResponsesDictionary = {
  vehicle_type: 'VEHICLE_TYPE',
  gross_weight_kg: 'VEHICLE_WEIGHT',
  first_registration_date: 'VEHICLE_AGE',
} as const;

// We should move this to utils.js when it supports Typescript
const getEntries = <T extends object>(obj: T) =>
  Object.entries(obj) as Entries<T>;

type GenerateDynamicResponsesParams = {
  currentTime: Date;
  defaultDynamicQuestions: any;
  vehicleData: any;
};

const generateDynamicResponses = ({
  currentTime,
  defaultDynamicQuestions,
  vehicleData,
}: GenerateDynamicResponsesParams) => {
  const dynamicQuestions: any[] = [];
  getEntries(ccResponsesDictionary).forEach(([key, value]) => {
    let newValue;
    const extraData = vehicleData[key];

    switch (key) {
      case 'first_registration_date':
        newValue =
          getYear(currentTime) - getYear(new Date(extraData as string));
        break;
      case 'vehicle_type':
        newValue = extraData;
        break;
      case 'gross_weight_kg':
        newValue = extraData ? extraData?.value : null;
        break;
      default:
        break;
    }

    if (!newValue) {
      const dynamicQuestion =
        defaultDynamicQuestions.find(
          (question: { id: string }) => question.id === value,
        ) || '';
      newValue = dynamicQuestion?.value;
    }
    if (newValue) {
      dynamicQuestions.push({
        id: value,
        value: newValue,
      });
    }
  });
  return dynamicQuestions;
};

type GenerateCoverageResponseParams = {
  caseData: any;
  consent: boolean;
  currentTime: Date;
  defaultDynamicQuestions: any;
  payload: any;
  staticQuestions: ComputableContractsQuestions[];
};

export const generateCoverageResponse = ({
  caseData,
  consent,
  currentTime,
  defaultDynamicQuestions,
  payload,
  staticQuestions,
}: GenerateCoverageResponseParams) => {
  const passengersData = caseData.passengers as PassengersData;
  const decisionTreeData =
    caseData.cause as unknown as DecisionTreeDataResultModel;
  const vehicleData = caseData.vehicle as VehicleDetailsData;

  const response = [
    {
      id: 'DISTANCE_TO_DESTINATION_ROAD',
      value: payload.distanceFromDestination?.value,
      type: 'DISTANCE',
      priority: 0,
    },
    {
      id: 'DISTANCE_FROM_HOME_ROAD',
      value: payload.distanceFromStart?.value,
      type: 'DISTANCE',
      priority: 0,
    },
    {
      id: 'INCIDENTS',
      value: decisionTreeData.code,
      type: 'ENUM',
      priority: 0,
    },
    {
      id: 'WEEKEND',
      value: isWeekend(currentTime),
      type: 'BOOLEAN',
      priority: 0,
    },
    {
      id: 'HOUR',
      value: getHours(currentTime),
      type: 'NUMBER',
      priority: 0,
    },
    {
      id: 'PREGNANT',
      value: passengersData?.details?.pregnancy || false,
      type: 'BOOLEAN',
      priority: 0,
    },
    {
      id: 'BABY',
      value: passengersData?.details?.baby || false,
      type: 'BOOLEAN',
      priority: 0,
    },
    {
      id: 'PET',
      value: passengersData?.details?.pets || false,
      type: 'BOOLEAN',
      priority: 0,
    },
    {
      id: 'DISABILITY',
      value: passengersData?.details?.disability || false,
      type: 'BOOLEAN',
      priority: 0,
    },
    {
      id: 'NUMBER_OF_PASSENGERS',
      value:
        passengersData &&
        passengersData.childrenNumber &&
        passengersData.passengersNumber
          ? passengersData.childrenNumber + passengersData.passengersNumber
          : 1,
      type: 'BOOLEAN',
      priority: 0,
    },
    {
      id: 'IMMOBILIZED_VEHICLE',
      value: consent,
      type: 'BOOLEAN',
      priority: 0,
    },
  ];

  staticQuestions.forEach((obj: ComputableContractsQuestions) => {
    response.push(obj);
  });

  const dynamicQuestions = generateDynamicResponses({
    vehicleData,
    defaultDynamicQuestions,
    currentTime,
  });
  response.push(...dynamicQuestions);

  return response;
};
